import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ListChecks, Map as MapIcon, Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { AvatarStack } from "@/components/common/UserAvatar";
import { OrderCard } from "@/components/orders/OrderCard";
import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingList } from "@/components/common/LoadingList";
import { OverviewMap } from "@/components/maps/OverviewMap";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { fmtDate, fmtMinutes } from "@/lib/format";
import { STATUS_LABELS, STATUS_ORDER, TEMPLATES } from "@/lib/i18n";
import { useIsDesktop } from "@/lib/useMediaQuery";
import { cn } from "@/lib/utils";

const ALL = "__alle__";

/** Segmentert statusfilter – rask filtrering med tommelen. */
const StatusSegments = ({ value, onChange, counts }) => (
  <div className="scrollbar-none -mx-4 flex gap-1 overflow-x-auto px-4 md:mx-0 md:px-0" role="tablist" aria-label="Filtrer på status" data-testid="ordrar-status-segments">
    {[ALL, ...STATUS_ORDER].map((s) => {
      const active = value === s;
      const n = s === ALL ? counts.total : counts[s] || 0;
      return (
        <button
          key={s}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(s)}
          className={cn(
            "focus-ring inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors duration-150",
            active ? "border-foreground bg-foreground text-background" : "border-border bg-card text-muted-foreground hover:border-brand-grey hover:text-foreground",
          )}
          data-testid={`ordrar-status-segment-${s === ALL ? "alle" : s}`}
        >
          {s === ALL ? "Alle" : STATUS_LABELS[s]}
          <span className={cn("tabular text-xs", active ? "text-brand-silver" : "text-muted-foreground")}>{n}</span>
        </button>
      );
    })}
  </div>
);

export default function OrdrarPage() {
  const { isAdmin } = useAuth();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(ALL);
  const [template, setTemplate] = useState(ALL);
  const [showMap, setShowMap] = useState(() => {
    const saved = window.localStorage.getItem("ordrar.showMap");
    return saved === null ? window.matchMedia("(min-width: 768px)").matches : saved === "1";
  });
  const toggleMap = () => setShowMap((v) => { window.localStorage.setItem("ordrar.showMap", v ? "0" : "1"); return !v; });

  const query = useQuery({ queryKey: ["orders", "all"], queryFn: async () => (await api.get("/orders")).data });

  const counts = useMemo(() => {
    const c = { total: (query.data || []).length };
    (query.data || []).forEach((o) => { c[o.status] = (c[o.status] || 0) + 1; });
    return c;
  }, [query.data]);

  const filtered = useMemo(() => {
    const text = q.trim().toLowerCase();
    return (query.data || []).filter((o) => {
      if (status !== ALL && o.status !== status) return false;
      if (template !== ALL && o.template !== template) return false;
      if (text) {
        const hay = `${o.order_number} ${o.title} ${o.customer_name || ""} ${o.site_address || ""}`.toLowerCase();
        if (!hay.includes(text)) return false;
      }
      return true;
    });
  }, [query.data, q, status, template]);

  const hasFilter = q || status !== ALL || template !== ALL;

  return (
    <div className="space-y-5" data-testid="ordrar-page">
      <PageHeader
        title="Ordrar"
        description={isAdmin ? "Alle oppdrag – opprett, tildel og følg opp." : "Oppdrag du er eller har vore tildelt."}
        actions={
          isAdmin ? (
            <Button className="h-11 flex-1 md:h-10 md:flex-none" asChild>
              <Link to="/ordrar/ny" data-testid="ordrar-ny-ordre-button"><Plus className="mr-2 h-4 w-4" /> Ny ordre</Link>
            </Button>
          ) : null
        }
      />

      <div className="space-y-3">
        <StatusSegments value={status} onChange={setStatus} counts={counts} />
        <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_220px_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk på ordrenummer, tittel, kunde eller stad" className="h-11 bg-card pl-9 pr-9 md:h-10" data-testid="ordrar-search-input" />
            {q ? <button type="button" onClick={() => setQ("")} className="focus-ring absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Tøm søk" data-testid="ordrar-search-clear"><X className="h-4 w-4" /></button> : null}
          </div>
          <Select value={template} onValueChange={setTemplate}>
            <SelectTrigger data-testid="ordrar-template-filter"><SelectValue placeholder="Mal" /></SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value={ALL}>Alle malar</SelectItem>
              {TEMPLATES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" className={cn("h-11 bg-card md:h-10", showMap && "border-foreground")} onClick={toggleMap} aria-pressed={showMap} data-testid="ordrar-map-toggle">
            <MapIcon className="mr-2 h-4 w-4" /> Kart
          </Button>
        </div>
      </div>

      {showMap && query.isSuccess ? <OverviewMap orders={filtered} heightClassName="h-56 md:h-80" testId="ordrar-map" /> : null}

      {query.isLoading ? <LoadingList /> : null}
      {query.isError ? <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} /> : null}
      {query.isSuccess && filtered.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="Ingen ordrar"
          description={hasFilter ? "Ingen ordrar passar filteret." : isAdmin ? "Opprett den første ordra." : "Du har ingen ordrar enno."}
          action={hasFilter ? <Button variant="outline" size="sm" className="bg-card" onClick={() => { setQ(""); setStatus(ALL); setTemplate(ALL); }} data-testid="ordrar-clear-filters">Nullstill filter</Button> : null}
          data-testid="ordrar-empty"
        />
      ) : null}

      {query.isSuccess && filtered.length > 0 && isDesktop ? (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <Table data-testid="ordrar-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-10 text-xs">Ordre</TableHead>
                <TableHead className="h-10 text-xs">Kunde</TableHead>
                <TableHead className="hidden h-10 text-xs xl:table-cell">Stad</TableHead>
                <TableHead className="h-10 text-xs">Planlagt</TableHead>
                <TableHead className="hidden h-10 text-xs lg:table-cell">Tildelt</TableHead>
                <TableHead className="h-10 text-right text-xs">Timar</TableHead>
                <TableHead className="h-10 text-xs">Status</TableHead>
                {isAdmin ? <TableHead className="h-10 text-xs">Fakturering</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((o) => (
                <TableRow key={o.id} className="cursor-pointer" onClick={() => navigate(`/ordrar/${o.id}`)} data-testid="ordrar-table-row">
                  <TableCell className="py-2.5">
                    <Link to={`/ordrar/${o.id}`} className="focus-ring block rounded-sm" onClick={(e) => e.stopPropagation()} data-testid="ordrar-table-link">
                      <span className="num block text-[11px] text-muted-foreground">{o.order_number} · {o.template_label}</span>
                      <span className="block font-medium leading-5">{o.title}</span>
                    </Link>
                  </TableCell>
                  <TableCell className="py-2.5">{o.customer_name}</TableCell>
                  <TableCell className="hidden max-w-[220px] truncate py-2.5 text-muted-foreground xl:table-cell">{o.logistics?.delivery?.name || o.site_address || "–"}</TableCell>
                  <TableCell className="tabular whitespace-nowrap py-2.5">{fmtDate(o.planned_date)}</TableCell>
                  <TableCell className="hidden py-2.5 lg:table-cell">
                    <span className="flex items-center gap-2">
                      <AvatarStack users={o.assigned_users || []} max={2} />
                      <span className="max-w-[160px] truncate text-muted-foreground">{o.assigned_users?.map((u) => u.name.split(" ")[0]).join(", ") || "–"}</span>
                    </span>
                  </TableCell>
                  <TableCell className="num whitespace-nowrap py-2.5 text-right">{fmtMinutes((o.stats?.minutes || 0) + (o.stats?.resource_minutes || 0))}</TableCell>
                  <TableCell className="py-2.5"><StatusBadge status={o.status} /></TableCell>
                  {isAdmin ? <TableCell className="py-2.5">{o.invoice_status && o.invoice_status !== "ingen" ? <StatusBadge kind="invoice" status={o.invoice_status} /> : <span className="text-xs text-muted-foreground">–</span>}</TableCell> : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}

      {query.isSuccess && filtered.length > 0 && !isDesktop ? (
        <div className="grid gap-2" data-testid="ordrar-card-list">
          {filtered.map((o) => <OrderCard key={o.id} order={o} showInvoice={isAdmin} testId="ordrar-order-card" />)}
        </div>
      ) : null}
    </div>
  );
}
